import AbodeForm from '../../../ordoro/AbodeForm/index.js'
import {packingListPrinterIDSelector} from '../../../data/printerSettings.js'
import {
  atLeastOneSplitOrderSelector,
  labelViewFormAbodeSelector,
  openAbodeTab,
  openSyncLoadingTab,
  sendAbodeRenderToPrintNode,
} from '../../../ordoro/AbodeForm/abodeFormFunctions.js'
import ButtonSendToPrinter from '../Button/ButtonSendToPrinter.js'
import ErrorMessage from '../ErrorMessage.js'
import {
  formsSelector,
  getState,
  onlyIfForm,
  removeForm,
  setForm,
  updateForm,
  useSelector,
} from '../../../store.js'
import {getUTCOffset} from '../../date.js'
import ConfirmModal from './ConfirmModal.js'
import {markDocumentsPrinted} from '../../../ordoro/OrderListPage/orderListActions.js'

const MODAL_FORM = 'PRINT_PICK_PACK_MODAL'
const PRINT_PICK_PACK_MODAL_FORM = 'PRINT_PICK_PACK_MODAL_FORM'

export function showPrintPickPackModal(orderNumbers) {
  setForm(MODAL_FORM, {
    orderNumbers,
    isPrinting: false,
    serverError: null,
  })
}

export function updateModalForm(...args) {
  updateForm(MODAL_FORM, ...args)
}

export function closeModal() {
  removeForm(MODAL_FORM)
}

export function modalFormSelector(state) {
  return formsSelector(state)[MODAL_FORM]
}

export async function viewPickPackDocument(tab, showPackingList) {
  if (!tab) {
    return
  }

  try {
    const {orderNumbers} = modalFormSelector(getState())
    updateModalForm({
      serverError: null,
      isPrinting: true,
    })

    await openAbodeTab({
      formName: PRINT_PICK_PACK_MODAL_FORM,
      orderNumbers,
      utcOffset: getUTCOffset(),
      docTypes: ['pick', 'pack'],
      tab,
    })

    if (showPackingList) {
      await markDocumentsPrinted(orderNumbers, {
        packing_list: true,
      })
    }

    closeModal()
  } catch (err) {
    updateModalForm({
      serverError: err.error_message || err.message,
      isPrinting: false,
    })
  }
}

async function handleButtonSentToPrintClick({
  canPrint,
  printButtonFormKey,
  showPackingList,
}) {
  // when we need to navigate to settings to setup printers
  if (!canPrint) {
    closeModal()

    return
  }

  try {
    const printerID = packingListPrinterIDSelector(getState())
    const {orderNumbers} = modalFormSelector(getState())

    updateModalForm({
      serverError: null,
    })

    await sendAbodeRenderToPrintNode({
      formName: PRINT_PICK_PACK_MODAL_FORM,
      orderNumbers,
      utcOffset: getUTCOffset(),
      docTypes: ['pick', 'pack'],
      printButtonFormKey,
      printerID,
      documentCount: orderNumbers.length,
    })

    if (showPackingList) {
      await markDocumentsPrinted(orderNumbers, {
        packing_list: true,
      })
    }

    closeModal()
  } catch (err) {
    updateModalForm({
      serverError: err.error_message || err.message,
    })
  }
}

function PrintPickPackModal({form}) {
  const docTypes = ['pick', 'pack']
  const {orderNumbers} = form

  const atLeastOneSplitOrder = useSelector((state) =>
    atLeastOneSplitOrderSelector(state, {
      orderNumbers,
    }),
  )
  const {showPickList, showPackingList} = useSelector((state) =>
    labelViewFormAbodeSelector(state, {
      formName: PRINT_PICK_PACK_MODAL_FORM,
    }),
  )
  const packingListPrinterID = useSelector(packingListPrinterIDSelector)

  const isDisabled = !showPickList && !showPackingList

  return (
    <ConfirmModal
      title="Print Pick/Pack Lists"
      modalSize="md"
      onConfirm={() => {
        const tab = openSyncLoadingTab()

        viewPickPackDocument(tab, showPackingList)
      }}
      onCancel={() => closeModal()}
      confirmText="View/Print"
      cancelText="Close"
      middleButtons={
        <div className="margin-right-10">
          <ButtonSendToPrinter
            className="margin-bottom-5"
            title="Pick/Pack List"
            printerID={packingListPrinterID}
            documentCount={orderNumbers.length}
            onClick={({canPrint, printButtonFormKey}) =>
              handleButtonSentToPrintClick({
                canPrint,
                printButtonFormKey,
                showPackingList,
              })
            }
            isDisabled={isDisabled}
          />
        </div>
      }
      isSaving={form.isPrinting}
      isDisabled={isDisabled}
      error={form.serverError}
    >
      <AbodeForm
        formName={PRINT_PICK_PACK_MODAL_FORM}
        docTypes={docTypes}
        fromModal
        includeSplitQuantitiesCheckbox={atLeastOneSplitOrder}
        allowPrintConfigs
      />
      {isDisabled && (
        <ErrorMessage>
          Choose which type of list you would like to print from the Document
          Options
        </ErrorMessage>
      )}
    </ConfirmModal>
  )
}

export default onlyIfForm(PrintPickPackModal, modalFormSelector)
